<template>
    <div>
        <dynamic-toolbar
            :loading="loading.lists"
            icon="mdi-clipboard-text-search"
            title="Maintenance Invoice Analysis"
            color="indigo"
            theme="dark"
        >
            <template #buttons>
                <v-btn
                    color="blue"
                    depressed
                    large
                    dark
                    :to="'/maintenance/invoices'"
                >
                    <v-icon left>mdi-invoice-list</v-icon>
                    Invoices
                </v-btn>
            </template>
        </dynamic-toolbar>
        <v-card elevation="0">
            <v-card-text>
                <v-card>
                    <v-card-text>
                        <v-row>
                            <v-col cols="12" md="2" align-self="center">
                                <v-text-field
                                    v-model="formattedCostTotal"
                                    label="Filtered Cost"
                                    :hint="
                                        costs.total != ''
                                            ? 'Total cost: ' + getFormattedField(costs.total)
                                            : 'Select a date range'
                                    "
                                    persistent-hint
                                    readonly
                                    outlined
                                    dense
                                    color="info"
                                    class="text--green font-weight-bold"
                                >
                                    <template v-if="loading.cost">
                                        <v-icon slot="append" size="24"
                                            >mdi-loading mdi-spin</v-icon
                                        >
                                    </template>
                                </v-text-field>
                            </v-col>
                            <!--Step 1-->
                            <v-col cols="12" md="2">
                                <v-text-field
                                    v-model="startDate"
                                    label="Start Date"
                                    hide-details
                                    dense
                                    color="indigo"
                                    prepend-icon="mdi-calendar"
                                    placeholder="YYYY-MM-DD"
                                    v-mask="'####-##-##'"
                                    :disabled="disableDateFields"
                                    @change="updateDateRange"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="2">
                                <v-text-field
                                    v-model="endDate"
                                    label="End Date"
                                    hide-details
                                    dense
                                    color="indigo"
                                    prepend-icon="mdi-calendar"
                                    placeholder="YYYY-MM-DD"
                                    v-mask="'####-##-##'"
                                    :disabled="disableDateFields"
                                    @change="updateDateRange"
                                ></v-text-field>
                            </v-col>
                            <!--<v-col cols="12" md="2">
                                <date-range-menu
                                    color="indigo"
                                    :hint="
                                        dateRange.length == 2
                                            ? 'Total cost: $' + costs.dateRange
                                            : 'Select a date range'
                                    "
                                    @update:dateRange="dateRange = $event"
                                />
                            </v-col>-->
                            <!--Step 2-->
                            <v-col cols="12" md="2" v-show="step >= 2">
                                <v-select
                                    v-model="invoiceType"
                                    :items="invoiceTypes"
                                    item-text="text"
                                    item-value="value"
                                    :hint="
                                        invoiceType
                                            ? 'Total cost by type: ' +
                                                getFormattedField(
                                                    invoiceTypes.find(
                                                        type =>
                                                            type.value == invoiceType
                                                    ).total_cost
                                                )
                                            : 'Select an invoice type'
                                    "
                                    persistent-hint
                                    label="Invoice Type"
                                    prepend-icon="mdi-file-chart"
                                    dense
                                    @change="setStep(3)"
                                >
                                    <template v-slot:item="{ item }">
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                {{ item.text }}
                                            </v-list-item-title>
                                            <v-list-item-subtitle>
                                                Total Cost:
                                                {{ getFormattedField(item.total_cost) }}
                                            </v-list-item-subtitle>
                                        </v-list-item-content>
                                    </template>
                                </v-select>
                            </v-col>
                            <!--Step 3-->
                            <v-col cols="12" md="4">
                                <v-select
                                    v-if="invoiceType && step >= 3"
                                    v-model="filterBy"
                                    :items="filters"
                                    item-text="text"
                                    item-value="value"
                                    label="Filter By"
                                    :hint="
                                        costs.extraFilters
                                            ? 'Total cost by filters: ' +
                                                getFormattedField(costs.extraFilters)
                                            : 'Select a filter'
                                    "
                                    persistent-hint
                                    prepend-icon="mdi-filter"
                                    multiple
                                    dense
                                    small-chips
                                    clearable
                                    @change="setStep(3)"
                                >
                                    <template v-slot:append>
                                        <v-btn
                                            v-if="filtersActive"
                                            color="primary"
                                            dark
                                            depressed
                                            @click="getReports"
                                        >
                                            <v-icon left>mdi-file-chart</v-icon>
                                            Filter
                                        </v-btn>
                                    </template>
                                </v-select>
                            </v-col>
                            <!--If filterBy is not empty show the button-->
                            <v-col cols="12" md="2" v-if="step >= 3">
                                <v-btn
                                    color="primary"
                                    dark
                                    depressed
                                    @click="setStep(3)"
                                >
                                    <v-icon left>mdi-reload</v-icon>
                                    Refresh Filter(s)
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col
                                cols="12"
                                md="2"
                                v-if="filterBy.includes('taxRegion')"
                            >
                                <v-autocomplete
                                    v-model="extraFilters.taxRegion"
                                    :items="taxRegions"
                                    item-text="name"
                                    item-value="id"
                                    clearable
                                    label="Tax Region"
                                    prepend-icon="mdi-map-marker-radius"
                                ></v-autocomplete>
                            </v-col>
                            <v-col
                                cols="12"
                                md="2"
                                v-if="filterBy.includes('vendor')"
                            >
                                <v-autocomplete
                                    v-model="extraFilters.vendor"
                                    :items="vendors"
                                    item-text="name"
                                    item-value="id"
                                    clearable
                                    label="Vendor"
                                    prepend-icon="mdi-account"
                                ></v-autocomplete>
                            </v-col>
                            <v-col
                                cols="12"
                                md="2"
                                v-if="filterBy.includes('company')"
                            >
                                <v-autocomplete
                                    v-model="extraFilters.company"
                                    :items="companies"
                                    item-text="CompanyName"
                                    item-value="CompanyID"
                                    clearable
                                    label="Company"
                                    prepend-icon="mdi-domain"
                                ></v-autocomplete>
                            </v-col>
                            <v-col
                                cols="12"
                                md="2"
                                v-if="filterBy.includes('user')"
                            >
                                <v-autocomplete
                                    v-model="extraFilters.user"
                                    :items="users"
                                    item-text="name"
                                    item-value="id"
                                    clearable
                                    label="User"
                                    prepend-icon="mdi-account"
                                ></v-autocomplete>
                            </v-col>
                            <v-col
                                cols="12"
                                md="2"
                                v-if="filterBy.includes('region')"
                            >
                                <v-autocomplete
                                    v-model="extraFilters.region"
                                    :items="regions"
                                    item-text="PrincipalRegion"
                                    item-value="PrincipalRegionID"
                                    clearable
                                    label="Region"
                                    prepend-icon="mdi-map-marker-radius"
                                ></v-autocomplete>
                            </v-col>
                            <v-col
                                cols="12"
                                md="2"
                                v-if="filterBy.includes('isServiceCall')"
                            >
                                <v-checkbox
                                    v-model="extraFilters.isServiceCall"
                                    label="Service Call"
                                    hide-details
                                ></v-checkbox>
                            </v-col>
                            <v-col
                                cols="12"
                                md="2"
                                v-if="filterBy.includes('isIncident')"
                            >
                                <v-checkbox
                                    v-model="extraFilters.isIncident"
                                    label="Incident"
                                    hide-details
                                ></v-checkbox>
                            </v-col>
                            <v-col
                                cols="12"
                                md="2"
                                v-if="filterBy.includes('isOOCharge')"
                            >
                                <v-checkbox
                                    v-model="extraFilters.isOOCharge"
                                    label="Owner Operator Charge"
                                    hide-details
                                ></v-checkbox>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="2" v-if="step >= 3">
                                <v-btn
                                    color="primary"
                                    dark
                                    depressed
                                    @click="downloadEquipmentByCostReport"
                                >
                                    <v-icon left>mdi-chart-bar</v-icon>
                                    "Equipment by cost" report
                                </v-btn>
                            </v-col>
                            <v-col cols="12" md="2" v-if="step >= 3">
                                <v-btn
                                    color="primary"
                                    dark
                                    depressed
                                    @click="downloadCategoriesByCostReport"
                                >
                                    <v-icon left>
                                        mdi-chart-bar
                                    </v-icon>
                                    "Categories by cost" report
                                </v-btn>
                            </v-col>
                            <v-col cols="12" md="2" v-if="step >= 3">
                                <v-btn
                                    color="primary"
                                    dark
                                    depressed
                                    @click="
                                        downloadEquipmentAndCategoriesByCostReport
                                    "
                                >
                                    <v-icon left>
                                        mdi-chart-tree
                                    </v-icon>
                                    "Equipment and Categories by cost" report
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
                <v-divider></v-divider>
                <v-row>
                    <v-col cols="12" md="12">
                        <v-list v-show="step == 3">
                            <v-list-item-group>
                                <v-list-item
                                    v-for="model in models"
                                    :key="model.id"
                                >
                                    <v-list-item-content>
                                        <v-list-item-title>
                                            <v-row>
                                                <v-col cols="6" md="2">
                                                    {{ model.name }}
                                                </v-col>
                                                <v-col cols="6" md="2">
                                                    Total Cost: {{
                                                        getFormattedField(model.total_cost)
                                                    }}
                                                </v-col>
                                            </v-row>
                                        </v-list-item-title>
                                        <v-list-item-subtitle>
                                            <v-progress-linear
                                                :value="model.percentage"
                                            ></v-progress-linear>
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                    <!--"By Category" button-->
                                    <v-list-item-action>
                                        <v-btn
                                            color="primary"
                                            dark
                                            depressed
                                            @click="byCategory(model)"
                                        >
                                            <v-icon left>mdi-tag</v-icon>
                                            By Category
                                        </v-btn>
                                    </v-list-item-action>
                                </v-list-item>
                            </v-list-item-group>
                        </v-list>
                        <div v-show="step == 4">
                            <v-list-item v-model="equipment">
                                <v-list-item-content>
                                    <v-list-item-title>
                                        {{ equipment.name }}
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        Total Cost: {{ getFormattedField(equipment.total_cost) }}
                                        <br />
                                        <v-progress-linear
                                            :value="equipment.percentage"
                                        ></v-progress-linear>
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                                <!--"By Category" button-->
                                <v-list-item-action>
                                    <v-btn
                                        color="primary"
                                        dark
                                        depressed
                                        @click="setStep(3, 'go-back')"
                                    >
                                        <v-icon left>mdi-arrow-left</v-icon>
                                        Go back
                                    </v-btn>
                                </v-list-item-action>
                            </v-list-item>
                            <v-divider></v-divider>
                            <v-expansion-panels>
                                <v-expansion-panel>
                                    <v-expansion-panel-header>
                                        <span class="indigo--text">Graphs</span>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <v-alert
                                            type="info"
                                            border="left"
                                            elevation="2"
                                            text
                                            icon="mdi-code-tags"
                                        >
                                            Feature in development
                                        </v-alert>
                                        <LineChartGenerator
                                            :chart-options="chartOptions"
                                            :chart-data="chartData"
                                            :width="100"
                                            :height="25"
                                        />
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>

                            <v-data-table
                                :headers="equipmentExpensesHeaders"
                                :items="equipmentExpensesReport"
                                disable-pagination
                                hide-default-footer
                                fixed-header
                                class="elevation-1"
                                group-by="maintenance_expense_category_id"
                            >
                                <template
                                    v-slot:group.header="{
                                        items,
                                        group
                                    }"
                                >
                                    <td>
                                        <v-btn
                                            text
                                            left
                                            color="indigo"
                                            @click="toggleGroup(group)"
                                        >
                                            <v-icon>
                                                {{
                                                    groupOpenState[group]
                                                        ? 'mdi-minus'
                                                        : 'mdi-plus'
                                                }}
                                            </v-icon>
                                            Category:
                                            {{ items[0].category.name }}
                                        </v-btn>

                                        <v-tooltip right color="success">
                                            <template
                                                v-slot:activator="{ on, attrs }"
                                            >
                                                <v-icon
                                                    right
                                                    color="success"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    @click="
                                                        downloadExpenseReport(
                                                            items
                                                        )
                                                    "
                                                >
                                                    mdi-file-chart
                                                </v-icon>
                                            </template>
                                            <span
                                                >Download
                                                {{ items[0].category.name }}
                                                Expenses report</span
                                            >
                                        </v-tooltip>
                                    </td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <!-- <td></td> -->
                                    <td>
                                        {{ expenseTotals(items).subTotalCost }}
                                    </td>
                                    <td>
                                        {{ expenseTotals(items).tax }}
                                    </td>
                                    <td>
                                        {{ expenseTotals(items).totalCost }}
                                    </td>
                                    <td>
                                        {{ getFormattedNumberField(items[0].OdometerKM, 0, true) }}
                                    </td>
                                    <td>
                                        {{ calculateCostPerKM(items, 4) }}
                                    </td>
                                </template>
                                <template v-slot:item="{ item }">
                                    <tr
                                        v-if="
                                            groupOpenState[
                                                item
                                                    .maintenance_expense_category_id
                                            ]
                                        "
                                    >
                                        <td>
                                            <v-tooltip right color="info">
                                                <template
                                                    v-slot:activator="{
                                                        on,
                                                        attrs
                                                    }"
                                                >
                                                    <span
                                                        v-bind="attrs"
                                                        v-on="on"
                                                        >{{
                                                            item.invoice_number
                                                                ? item.invoice_number
                                                                : 'N/A'
                                                        }}</span
                                                    >
                                                    <v-icon
                                                        right
                                                        color="info"
                                                        v-bind="attrs"
                                                        v-on="on"
                                                        @click="
                                                            viewInvoice(
                                                                item.invoice_id
                                                            )
                                                        "
                                                    >
                                                        {{
                                                            item.invoice_number
                                                                ? 'mdi-card-search-outline'
                                                                : ''
                                                        }}
                                                    </v-icon>
                                                </template>
                                                <span>View Invoice</span>
                                            </v-tooltip>
                                        </td>
                                        <td>{{ getDateFromDatetime(item.invoice_issued_at) }}</td>
                                        <td>{{ item.vendor }}</td>
                                        <td>
                                            {{ item.category.name }}
                                        </td>
                                        <td>
                                            {{
                                                item.subcategory
                                                    ? item.subcategory.name
                                                    : 'N/A'
                                            }}
                                        </td>
                                        <td>{{ item.description }}</td>
                                        <!-- <td>{{ item.custom_tax_rate }}</td> -->
                                        <td>{{ item.quantity }}</td>
                                        <td>{{ getFormattedField(item.unit_cost) }}</td>
                                        <td>
                                            {{
                                                item.subtotal_cost
                                                    ? getFormattedField(item.subtotal_cost)
                                                    : expenseTotals(item)
                                                          .subTotalCost
                                            }}
                                        </td>
                                        <!-- <td>{{ item.tax }}</td> -->
                                        <td>{{ getFormattedField(item.total_cost) }}</td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </template>
                                <template v-slot:foot="{ items }">
                                    <td>
                                        <v-btn
                                            color="success"
                                            text
                                            class="ma-2"
                                            @click="
                                                downloadExpenseReport(items)
                                            "
                                        >
                                            <v-icon left
                                                >mdi-chart-box-multiple</v-icon
                                            >
                                            Download Expense Report
                                        </v-btn>
                                    </td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td class="px-4">
                                        <strong>{{ expenseTotals(items).subTotalCost }}</strong>
                                    </td>
                                    <!-- <td class="px-4">
                                        <strong>{{ expenseTotals(items).tax }}</strong>
                                    </td> -->
                                    <td class="px-4">
                                        <strong>{{ expenseTotals(items).totalCost }}</strong>
                                    </td>
                                    <td></td>
                                    <td class="px-4">
                                        <strong>{{ calculateCostPerKM(items, 4) }}</strong>
                                    </td>
                                </template>
                            </v-data-table>
                        </div>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
        <v-dialog
            v-if="invoiceDialog"
            v-model="invoiceDialog"
            max-width="90vw"
            persistent
            fullscreen
            transition="dialog-bottom-transition"
        >
            <v-card>
                <v-card-title style="margin-bottom: 20px;">
                    <v-row class="d-flex align-center" style="justify-content: space-between;">
                        <v-col cols="auto">
                            <h3>Invoice: {{ typeof invoice.invoice_number !== 'undefined' ? invoice.invoice_number : '' }}</h3>
                        </v-col>
                        <v-col cols="auto">
                            <v-icon
                                color="indigo"
                                @click="closeInvoiceData"
                            >
                                mdi-close-circle
                            </v-icon>
                        </v-col>
                        <!-- <v-col cols="12" md="6">
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-btn
                                        color="info"
                                        dark
                                        depressed
                                        @click="
                                            downloadExpenseReport(
                                                invoice.expenses
                                            )
                                        "
                                    >
                                        <v-icon left>mdi-file-download</v-icon>
                                        Download
                                    </v-btn>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-btn
                                        color="error"
                                        dark
                                        depressed
                                        @click="invoiceDialog = false"
                                    >
                                        <v-icon left>mdi-close</v-icon>
                                        Close
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-col> -->
                    </v-row>
                </v-card-title>
                <v-card-text v-if="invoice != {}">
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="invoice.invoice_number"
                                        label="Invoice Number"
                                        readonly
                                        outlined
                                        dense
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="invoice.purchase_order_number"
                                        label="Purchase Order Number"
                                        readonly
                                        outlined
                                        dense
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="formattedInvoiceSubtotalCost"
                                        label="Subtotal Cost"
                                        readonly
                                        outlined
                                        dense
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="formattedInvoiceTotalCost"
                                        label="Total Cost"
                                        readonly
                                        outlined
                                        dense
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="invoice.invoice_issued_at"
                                        label="Invoice Date"
                                        readonly
                                        outlined
                                        dense
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6" v-if="invoiceType !== 'supplies'">
                                    <v-text-field
                                        v-if="invoiceType == 'trucks'"
                                        v-model="invoice.model.Truck"
                                        label="Unit"
                                        readonly
                                        outlined
                                        dense
                                    ></v-text-field>
                                    <v-text-field
                                        v-else-if="invoiceType == 'trailers'"
                                        v-model="invoice.model.Trailer"
                                        label="Unit"
                                        readonly
                                        outlined
                                        dense
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="invoice.maintenance_ended_at"
                                        label="Maintenance Ended At"
                                        readonly
                                        outlined
                                        dense
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="invoice.maintenance_started_at"
                                        label="Maintenance Started At"
                                        readonly
                                        outlined
                                        dense
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="invoice.tax_region.name"
                                        label="Tax Region"
                                        readonly
                                        outlined
                                        dense
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="invoice.region.PrincipalRegion"
                                        label="Region"
                                        readonly
                                        outlined
                                        dense
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="invoice.company.CompanyName"
                                        label="Company"
                                        readonly
                                        outlined
                                        dense
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="invoice.vendor.name"
                                        label="Vendor"
                                        readonly
                                        outlined
                                        dense
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="invoice.user.name"
                                        label="User"
                                        readonly
                                        outlined
                                        dense
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="invoice.hub_reading"
                                        label="Hub Reading"
                                        readonly
                                        outlined
                                        dense
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="6">
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-btn
                                        class="ma-2"
                                        outlined
                                        color="teal"
                                        @click="viewInvoiceFile(invoice)"
                                    >
                                        View invoice file
                                        <v-icon
                                            right
                                            dark
                                        >
                                            mdi-file-eye-outline
                                        </v-icon>
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                    <v-divider></v-divider>
                    <v-row>
                        <v-col cols="12" md="12">
                            <v-data-table
                                fixed-header
                                :headers="expenseHeaders"
                                :items="invoice.expenses"
                                disable-pagination
                                hide-default-footer
                                class="elevation-1"
                                group-by="maintenance_expense_category_id"
                            >
                                <template
                                    v-slot:group.header="{ items, group }"
                                    style="cursor: crosshair; background-color: #e8eaf6"
                                >
                                    <td style="text-align: left;">
                                        <v-btn
                                            text
                                            left
                                            color="indigo"
                                            @click="toggleGroup(group)"
                                        >
                                            <v-icon>
                                                {{
                                                    groupOpenState[group]
                                                        ? 'mdi-minus'
                                                        : 'mdi-plus'
                                                }}
                                            </v-icon>
                                            Category:
                                            {{ items[0].category.name }}
                                        </v-btn>
                                        <!-- <v-tooltip right color="success">
                                            <template
                                                v-slot:activator="{ on, attrs }"
                                            >
                                                <v-icon
                                                    right
                                                    color="success"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    @click="
                                                        downloadExpenseReport(
                                                            items
                                                        )
                                                    "
                                                >
                                                    mdi-file-chart
                                                </v-icon>
                                            </template>
                                            <span
                                                >Download
                                                {{ items[0].category.name }}
                                                Expenses report</span
                                            >
                                        </v-tooltip> -->
                                    </td>
                                    <!-- <td></td> -->
                                    <!-- <td></td> -->
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td style="text-align: left;">
                                        {{ expenseTotals(items).subTotalCost }}
                                    </td>
                                    <td style="text-align: left;">{{ expenseTotals(items).tax }}</td>
                                    <td style="text-align: left;">
                                        {{ expenseTotals(items).totalCost }}
                                    </td>
                                </template>
                                <template v-slot:item="{ item }">
                                    <tr
                                        v-if="
                                            groupOpenState[
                                                item
                                                    .maintenance_expense_category_id
                                            ]
                                        "
                                    >
                                        <td style="text-align: left;">{{ item.category.name }}</td>
                                        <td style="text-align: left;">
                                            {{
                                                item.subcategory
                                                    ? item.subcategory.name
                                                    : 'N/A'
                                            }}
                                        </td>
                                        <td style="text-align: left;">{{ item.description }}</td>
                                        <!-- <td style="text-align: left;">{{ item.custom_tax_rate }}</td> -->
                                        <td style="text-align: left;">{{ item.quantity }}</td>
                                        <td style="text-align: left;">{{ getFormattedField(item.unit_cost) }}</td>
                                        <td style="text-align: left;">
                                            {{
                                                item.subtotal_cost
                                                    ? getFormattedField(item.subtotal_cost)
                                                    : expenseTotals(item)
                                                          .subTotalCost
                                            }}
                                        </td>
                                        <!-- <td style="text-align: left;">{{ item.tax }}</td> -->
                                        <td style="text-align: left;">{{ getFormattedField(item.total_cost) }}</td>
                                    </tr>
                                </template>
                                <template v-slot:foot="{ items }">
                                    <!-- <td>
                                        <v-btn
                                            color="success"
                                            text
                                            class="ma-2"
                                            @click="
                                                downloadExpenseReport(items)
                                            "
                                        >
                                            <v-icon left
                                                >mdi-chart-box-multiple</v-icon
                                            >
                                            Download Expense Report
                                        </v-btn>
                                    </td> -->
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <!-- <td></td> -->
                                    <!-- <td></td> -->
                                    <td></td>
                                    <td class="px-4" style="text-align: left;">
                                        <strong>{{ expenseTotals(items).subTotalCost }}</strong>
                                    </td>
                                    <td class="px-4" style="text-align: left;">
                                        <strong>{{ expenseTotals(items).tax }}</strong>
                                    </td>
                                    <td class="px-4" style="text-align: left;">
                                        <strong>{{ expenseTotals(items).totalCost }}</strong>
                                    </td>
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>
        <v-dialog v-model="invoiceFileView" fullscreen>
            <v-card>
                <v-toolbar color="secondary" dense dark>
                    <v-spacer></v-spacer>
                    <v-card-title primary-title>Invoice Document View</v-card-title>
                    <v-spacer></v-spacer>
                    <v-btn dark icon left @click="invoiceFileView = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-toolbar>
                <v-container grid-list-xs fluid>
                    <iframe :src="invoiceFileLink" style="width: 100%; height:100vh ;"></iframe>
                </v-container>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { Line as LineChartGenerator, generateChart } from 'vue-chartjs/legacy';

import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    LineElement,
    LinearScale,
    CategoryScale,
    PointElement
} from 'chart.js';

ChartJS.register(
    Title,
    Tooltip,
    Legend,
    LineElement,
    LinearScale,
    CategoryScale,
    PointElement
);

function logErr(error) {
    console.log('Axios error: ', error);
}

import axios from 'axios';
import DynamicToolbar from '../../components/Global/Utils/DynamicToolbar.vue';
import DateRangeMenu from '../../components/Global/Utils/DateRangeMenu.vue';

import { mask } from 'vue-the-mask';
import { sheets } from '@/Utils/dataSheets';
import { xlsxSheets } from '@/Utils/xlsxSheets';
export default {
    directives: { mask },
    name: 'MaintenanceReports',
    components: {
        LineChartGenerator,
        DateRangeMenu,
        DynamicToolbar
    },
    data: () => ({
        chartData: {
            labels: [],
            datasets: [
                {
                    label: 'Total Cost',
                    data: [],
                    borderColor: '#9ACFF9',
                    backgroundColor: '#42A5F0',
                    borderWidth: 1,
                    tension: 0.2
                },
                {
                    label: 'Total Cost by Month',
                    data: [],
                    borderColor: '#F9D9AC',
                    backgroundColor: '#F0A542',
                    borderWidth: 1,
                    tension: 0.2
                }
            ]
        },
        chartOptions: {
            responsive: true,
            interaction: {
                intersect: false
            }
        },
        auth: {},
        //Date range
        dateRange: [],
        panel: [],
        dateRangeDisplay: '',
        dateRangeStart: '',
        dateRangeEnd: '',
        costs: {
            total: 0,
            filtered: 0,
            dateRange: 0,
            invoiceType: 0,
            equipment: 0,
            extraFilters: 0,
            category: 0,
            subcategory: 0
        },
        dateType: 'invoice_issued_at',
        invoiceType: '',
        dateTypes: [
            { text: 'Invoice Date', value: 'invoice_issued_at' },
            { text: 'Created At', value: 'created_at' }
        ],
        invoiceTypes: [
            { text: 'Trailers', value: 'trailers', total_cost: 0 },
            { text: 'Trucks', value: 'trucks', total_cost: 0 },
            { text: 'Supplies', value: 'supplies', total_cost: 0 }
            /*{ text: 'All', value: '', disabled: true },*/
        ],
        filters: [
            //{ text: 'Filter by Equipment', value: 'equipment' },
            //{ text: 'Filter by Category/SubCategory', value: 'category' },
            { text: 'Tax Region', value: 'taxRegion' },
            { text: 'Vendor', value: 'vendor' },
            { text: 'Company', value: 'company' },
            { text: 'Region', value: 'region' },
            { text: 'Service Call', value: 'isServiceCall' },
            { text: 'Incident', value: 'isIncident' },
            { text: 'OO Charge', value: 'isOOCharge' }
        ],
        extraFilters: {
            taxRegion: '',
            vendor: '',
            company: '',
            region: '',
            user: '',
            isServiceCall: false,
            isIncident: false,
            isOOCharge: false
        },
        filtersActive: false,
        sortBy: 'total_cost',
        sortOptions: [
            { text: 'Total Cost', value: 'total_cost' },
            { text: 'Date', value: 'invoice_issued_at' }
        ],
        loadOnlyMatchingExpenses: false,
        dateRangeMenu: false,

        //loaders
        loading: {
            lists: true,
            cost: false,
            dateRangeTotalCost: false,
            totalCostByType: false,
            totalCostByEquipment: false,
            totalCostByCategory: false,
            totalCostBySubcategory: false,
            report: false
        },

        //Axios data
        trailers: [],
        trucks: [],
        models: [],
        rentAgreements: [],
        expenseTypes: [],
        expenseCategories: [],
        expenseCategoriesFiltered: [],
        expenseSubCategories: [],
        expenseSubCategoriesFiltered: [],
        taxRegions: [],
        vendors: [],
        companies: [],
        regions: [],

        //Selected data
        trailer: '',
        truck: '',
        taxRegion: '',
        vendor: '',
        company: '',
        region: '',
        type: '',
        category: '',
        subcategory: '',
        isServiceCall: false,
        isIncident: false,
        isOOCharge: false,
        filterBy: '',

        //Steps
        steps: {
            dateRange: false,
            invoiceType: false,
            filterBy: false,
            equipment: false,
            category: false,
            subcategory: false
        },
        step: 1,

        //Report data
        showTable: false,
        showEquipmentExpensesTable: false,
        report: [],
        reportHeaders: [],
        expanded: [],
        expenseHeaders: [
            {
                text: 'Expense Category',
                value: 'category'
            },
            {
                text: 'Expense Subcategory',
                value: 'subcategory'
            },
            { text: 'Description', value: 'description' },
            // { text: 'Tax Rate', value: 'custom_tax_rate' },
            { text: 'Quantity', value: 'quantity' },
            { text: 'Unit Cost', value: 'unit_cost' },
            { text: 'Subtotal Cost', value: 'subtotal_cost' },
            // { text: 'Tax', value: 'tax' },
            { text: 'Total Cost', value: 'total_cost' }
        ],
        //Add the "invoice_number" to the expense headers
        equipmentExpensesHeaders: [
            { text: 'Invoice Number', value: 'invoice_number' },
            { text: 'Invoice Date', value: 'invoice_issued_at' },
            { text: 'Vendor', value: 'vendor' },
            {
                text: 'Expense Category',
                value: 'category.name'
            },
            {
                text: 'Expense Subcategory',
                value: 'subcategory.name'
            },
            { text: 'Description', value: 'description' },
            // { text: 'Tax Rate', value: 'custom_tax_rate' },
            { text: 'Quantity', value: 'quantity' },
            { text: 'Unit Cost', value: 'unit_cost' },
            { text: 'Subtotal Cost', value: 'subtotal_cost' },
            // { text: 'Tax', value: 'tax' },
            { text: 'Total Cost', value: 'total_cost' },
            { text: 'OdometerKM', value: 'OdometerKM' },
            { text: 'CostPerKM', value: 'CostPerKM' },
        ],
        equipmentExpensesSheetHeaders: [
            { text: 'ID', value: 'id' },
            { text: 'Invoice Number', value: 'invoice_number' },
            { text: 'Invoice ID', value: 'invoice_id' },
            { text: 'Vendor', value: 'vendor' },
            { text: 'Category', value: 'category.name' },
            { text: 'Subcategory', value: 'subcategory.name' },
            { text: 'Description', value: 'description' },
            { text: 'Tax Rate', value: 'custom_tax_rate' },
            { text: 'Quantity', value: 'quantity' },
            { text: 'Unit Cost', value: 'unit_cost' },
            { text: 'Subtotal Cost', value: 'subtotal_cost' },
            { text: 'Tax', value: 'tax' },
            { text: 'Total Cost', value: 'total_cost' },
            { text: 'Invoice Date', value: 'invoice_issued_at' }
        ],
        equipmentExpensesReport: [],
        equipmentExpensesArray: [], //Array of the total cost for every expense sorted by date (oldest to newest)
        equipmentExpensesArrayLabels: [], //Array of the dates for the expenses
        groupOpenState: {},
        equipment: {},

        //View invoice
        invoiceId: '',
        invoice: {},
        invoiceDialog: false,

        startDate: "",
        endDate: "",
        disableDateFields: true,
        externalSource: false,
        equipmentId: 0,
        odometerKM: 0,
        regionId: '',
        invoiceFileView: false,
        invoiceFileLink: '',
    }),
    mounted() {
        this.dateRangeDisplay = 'Select a date range';
        this.authentication = {
            headers: {
                Authorization: 'Bearer ' + this.$store.state.token
            }
        };
        this.getReportData();

        const query = this.$route.query;
        const externalSource = query.externalSource || 'false';
        const startDate = query.startDate || '';
        const endDate = query.endDate || '';
        const invoiceType = query.invoiceType || '';
        const equipmentId = query.equipmentId || '';
        const odometerKM = query.odometerKM || '';
        const regionId = query.regionId || '';
        console.log("Rolling-12 Query", query);
        if (externalSource === 'true') {
            this.externalSource = true;
            this.startDate = startDate;
            this.endDate = endDate;
            this.invoiceType = invoiceType;
            this.equipmentId = equipmentId;
            this.odometerKM = odometerKM;
            this.regionId = regionId;
            if (this.regionId !== '') {
                this.filterBy = 'region';
                this.extraFilters.region = parseInt(this.regionId);
                console.log('Regions: ', this.regions);
                console.log('Filters: ', this.filters);
                console.log('Extra Filters: ', this.extraFilters);
            }
            console.table([
                [ "startDate", this.startDate ],
                [ "endDate", this.endDate ],
                [ "invoiceType", this.invoiceType ],
                [ "equipmentId", equipmentId ],
                [ "odometerKM", this.odometerKM ],
                [ "regionId", this.regionId ],
            ]);
            this.updateDateRange();
            this.setStep(3);
        }
    },
    computed: {
        cardStyle() {
            if (this.$refs.toolbar) {
                let toolbarHeight = this.$refs.toolbar.$el.clientHeight;
                return {
                    position: 'sticky',
                    top: toolbarHeight + 'px',
                    zIndex: 1,
                    backgroundColor: 'white'
                };
            }
            return {
                position: 'sticky',
                top: '35px',
                zIndex: 1,
                backgroundColor: 'white'
            };
        },
        formattedCostTotal: {
            get() {
                return this.getFormattedField(this.costs.total);
            },
        },
        formattedInvoiceSubtotalCost: {
            get() {
                return this.getFormattedField(this.invoice.subtotal_cost);
            },
        },
        formattedInvoiceTotalCost: {
            get() {
                return this.getFormattedField(this.invoice.total_cost);
            },
        }
    },
    methods: {
        calculateCostPerKM(items, decimals = 2) {
            let totalCost = this.expenseTotals(items, false).totalCost;
            let odometerKM = items[0].OdometerKM;
            const formatter = new Intl.NumberFormat('en-CA', {
                style: 'currency',
                currency: 'CAD',
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            });
            if (odometerKM == null || odometerKM == 0) {
                return "N/A";
            } else {
                return formatter.format(totalCost / odometerKM);
            }
        },
        updateDateRange() {
            console.log(this.startDate, this.endDate);
            if (
                this.startDate != "" &&
                this.endDate != "" &&
                this.isValidDate(this.startDate) &&
                this.isValidDate(this.endDate) &&
                this.startDate < this.endDate
            ) {
                this.setStep(2);
            }
        },
        isValidDate(dateString) {
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

            // Check if the input matches the regex pattern
            if (!dateRegex.test(dateString)) {
                return false;
            }

            // Parse the date components
            const [year, month, day] = dateString.split('-').map(Number);

            // Create a date object from the input
            const date = new Date(year, month - 1, day);

            // Check if the date is valid
            if (
                date.getFullYear() !== year ||
                date.getMonth() !== month - 1 ||
                date.getDate() !== day
            ) {
                return false;
            }

            return true;
        },
        toggleGroup(group) {
            console.log('Group: ', group);
            if (
                Object.prototype.hasOwnProperty.call(this.groupOpenState, group)
            ) {
                this.groupOpenState[group] = !this.groupOpenState[group];
                console.log('Group open state: ', this.groupOpenState);
            }
        },
        setDateRange() {
            let dateRange = this.dateRange;
            if (dateRange == '') {
                this.dateRangeStart = '';
                this.dateRangeEnd = '';
            } else {
                //If the end date is less than the start date, swap them
                if (dateRange[1] < dateRange[0]) {
                    let temp = dateRange[0];
                    dateRange[0] = dateRange[1];
                    dateRange[1] = temp;
                }
                this.dateRangeStart = dateRange[0];
                this.dateRangeEnd = dateRange[1];
            }
            this.dateRangeDisplay = `${this.dateRangeStart} ~ ${this.dateRangeEnd}`;
        },
        setStep(step, source = "") {
            console.log('Step: ', step);
            /**
             * First step: date range
             * Second step: invoice type
             * Third step: filter by
             * Fourth step: equipment
             *
             * If the step is 1, set the data from the next steps to empty
             */
            if (step == 2) {
                //In the first step we set the date range, unset the variables for the next steps
                this.getDateRangeTotalCost();
                //Unset the variables for the next steps
                this.trailers = [];
                this.trucks = [];
                this.models = [];

                if (!this.externalSource) {
                    this.invoiceType = '';
                }
            }
            if (step == 3) {
                if (source == "go-back" && this.externalSource) {
                    this.externalSource = false;
                    this.equipmentId = 0;
                    this.odometerKM = 0;
                    const routePath = this.$route.path;
                    window.history.replaceState({}, '', routePath);
                }

                //In the second step we set the invoice type, unset the variables for the next steps
                //If the trailers array is empty, get the trailers
                if (this.models.length <= 0 || this.step < 3) {
                    //Get the trailers
                    console.log("from step 3");
                    this.getEquipmentByCost();
                }
            }
            if (step == 4) {
                //In the third step we set the filter by, unset the variables for the next steps
            }
            this.step = step;
        },

        byCategory(equipment) {
            //Show a list of expenses for the selected equipment by category sorted by total cost per category
            this.equipment = equipment;
            console.log('equipment from by category', this.equipment);
            this.showEquipmentExpensesTable = false;
            this.loading.lists = true;
            this.equipmentExpensesReport = [];
            this.costs.filtered = equipment.total_cost;
            let equipmentId = this.equipment.id;
            let url = '';
            // let dateRange = this.dateRange;
            // let startDate = dateRange[0];
            // let endDate = dateRange[1];
            let validFilters = this.processFilters();
            url =
                '/v2/maintenance/reports/equipment-total-cost/' +
                this.invoiceType + //truck or trailer
                '/' +
                equipmentId +
                '/' +
                this.startDate +
                '/' +
                this.endDate +
                '/' +
                (this.externalSource ? 1 : 0);
            if (validFilters != '') {
                url += validFilters;
            }

            console.table([
                [ "InvoiceType", this.invoiceType ],
                [ "EquipmentId", equipmentId ],
                [ "StartDate", this.startDate ],
                [ "EndDate", this.endDate ],
            ]);

            axios
                .get(url, this.authentication)
                .then(response => {
                    this.setStep(4);
                    let expenseData = response.data.expenses;
                    let odometerKm = response.data.odometerKm;
                    if (this.externalSource) {
                        for (let exp of expenseData) {
                            exp.OdometerKM = this.getFormattedNumberField(this.odometerKM, 0);
                        }
                    } else {
                        for (let exp of expenseData) {
                            exp.OdometerKM = this.getFormattedNumberField(odometerKM, 0);
                        }
                    }
                    this.equipmentExpensesReport = expenseData;
                    console.log("equipmentExpensesReport", this.equipmentExpensesReport);
                    this.setReportHeaders();
                    this.showEquipmentExpensesTable = true;
                    this.$store.state.isLoading = false;
                    this.loading.lists = false;
                    if (this.equipmentExpensesReport.length == 0) {
                        this.$store.dispatch('setSnackbar', {
                            color: 'warning',
                            text: 'No data found'
                        });
                    } else {
                        this.equipmentExpensesReport.forEach(item => {
                            this.$set(
                                this.groupOpenState,
                                item.maintenance_expense_category_id,
                                false
                            );
                        });
                    }
                })
                .catch(error => {
                    this.$store.state.isLoading = false;
                    this.$store.dispatch('setSnackbar', {
                        color: 'error',
                        text: 'Error getting report data' + error
                    });
                });
        },

        generateChartData(data) {
            //

            //Sort the data by date "invoice_issued_at", merge the expenses with the same date. Concatenate the values of the expenses with the same date (total_cost, tax, subtotal_cost, etc.)
            let mergedData = data.reduce((acc, current) => {
                const x = acc.find(
                    item => item.invoice_issued_at === current.invoice_issued_at
                );
                if (!x) {
                    return acc.concat([current]);
                } else {
                    return acc.map(item =>
                        item.invoice_issued_at === current.invoice_issued_at
                            ? {
                                  ...item,
                                  total_cost:
                                      item.total_cost + current.total_cost,
                                  tax: item.tax + current.tax,
                                  subtotal_cost:
                                      item.subtotal_cost + current.subtotal_cost
                              }
                            : item
                    );
                }
            }, []);

            //Generate the equipmentExpensesArray, turn the total_cost into a number and add it to the array, set invalid values to 0

            let dailyData = mergedData.map(expense => {
                let expenseTotalCost = parseFloat(expense.total_cost);
                let expenseTax = parseFloat(expense.tax);
                let expenseSubTotalCost = parseFloat(expense.subtotal_cost);
                let expenseDate = expense.invoice_issued_at;
                let expenseDateLabel = expenseDate.split('T')[0];
                dailyLabels.push(expenseDateLabel);
                return {
                    date: expenseDateLabel,
                    totalCost: expenseTotalCost,
                    tax: expenseTax,
                    subTotalCost: expenseSubTotalCost
                };
            });
            //Group the expenses by month based on the date from "invoice_issued_at"
            // Group the expenses by month based on the date from "invoice_issued_at"
            let monthlyData = data.reduce((acc, current) => {
                let date = new Date(current.invoice_issued_at);
                let month = date.getMonth() + 1;
                let year = date.getFullYear();
                let monthYear = month + '/' + year;
                if (!acc[monthYear]) {
                    acc[monthYear] = [];
                }
                acc[monthYear].push(current);
                return acc;
            }, {});

            let monthlyLabels = Object.keys(monthlyData);

            this.chartData.labels = dailyLabels;
            this.chartData.datasets[0].data = dailyData.map(expense => {
                return expense.totalCost;
            });
            this.chartData.datasets[1].data = monthlyLabels.map(month => {
                let totalCost = 0;
                monthlyData[month].forEach(expense => {
                    totalCost += parseFloat(expense.total_cost);
                });
                return totalCost;
            });
        },

        expenseCategoriesFilteredByType(expenseType) {
            if (!expenseType) return [];
            this.expenseCategoriesFiltered = this.expenseCategories.filter(
                expenseCategory =>
                    expenseCategory.maintenance_expense_type_id ==
                    expenseType.id
            );
        },

        expenseSubCategoriesFilteredByCategory(expenseCategoryId) {
            if (!expenseCategoryId) {
                this.expenseSubCategoriesFiltered = [];
                return [];
            }
            this.expenseSubCategoriesFiltered = this.expenseSubCategories.filter(
                expenseSubcategory =>
                    expenseSubcategory.maintenance_expense_category_id ==
                    expenseCategoryId
            );
        },
        expenseTotals(expenses, format = true) {
            //Return total costs and taxes for the expenses
            let totalCost = 0;
            let tax = 0;
            let subTotalCost = 0;
            //if there are multiple expenses run the foreach loop to get the total cost and tax else process the single expense that is not an array
            if (expenses.length > 0) {
                expenses.forEach(expense => {
                    //values are in text format, convert to numbers of no more than 2 decimal places, remember that these are currencies
                    let expenseTotalCost = parseFloat(expense.total_cost);
                    let expenseTax = parseFloat(expense.tax);
                    let expenseSubTotalCost = parseFloat(expense.subtotal_cost);
                    totalCost += expenseTotalCost;
                    tax += expenseTax;
                    subTotalCost += expenseSubTotalCost;
                });
            } else {
                let expense = expenses;
                let expenseTotalCost = parseFloat(expense.total_cost);
                let expenseTax = parseFloat(expense.tax);
                let expenseSubTotalCost = parseFloat(expense.subtotal_cost);
                totalCost += expenseTotalCost;
                tax += expenseTax;
                subTotalCost += expenseSubTotalCost;
            }
            totalCost = Math.round(totalCost * 100) / 100;
            tax = Math.round(tax * 100) / 100;
            subTotalCost = Math.round(subTotalCost * 100) / 100;
            const formatter = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
            });

            if (format) {
                return {
                    subTotalCost: formatter.format(subTotalCost),
                    totalCost: formatter.format(totalCost),
                    tax: formatter.format(tax)
                };
            } else {
                return {
                    subTotalCost: subTotalCost,
                    totalCost: totalCost,
                    tax: tax
                };
            }
        },
        getDateRangeTotalCost() {
            this.loading.lists = true;
            // let dateRange = this.dateRange;
            let url = '';
            if (this.startDate == "" || this.endDate == "") {
                this.$store.dispatch('setSnackbar', {
                    color: 'error',
                    text: 'Please select a date range'
                });
                return;
            }
            // let startDate = dateRange[0];
            // let endDate = dateRange[1];
            url =
                '/v2/maintenance/reports/total-cost-by-dates/' +
                this.startDate +
                '/' +
                this.endDate;

            axios
                .get(url, this.authentication)
                .then(response => {
                    console.log("getDateRangeTotalCost");
                    this.costs.dateRange = response.data.total;
                    this.costs.total = response.data.total;
                    this.costs.filtered = response.data.total;
                    //Set the total cost for each invoice type
                    this.invoiceTypes.forEach(invoiceType => {
                        invoiceType.total_cost =
                            response.data[invoiceType.value];
                    });
                    if (!this.externalSource) {
                        this.loading.lists = false;
                    }
                    this.dateRangeMenu = false;
                    console.table([
                        ["costs.dateRange", this.costs.dateRange],
                        ["costs.total", this.costs.total],
                        ["costs.filtered", this.costs.filtered],
                        ["invoiceType", this.invoiceType]
                    ]);
                })
                .catch(error => {
                    this.$store.dispatch('setSnackbar', {
                        color: 'error',
                        text: 'Error getting report data' + error
                    });
                });
        },
        processFilters() {
            let validFilters = '?';
            for (let filter in this.extraFilters) {
                if (
                    this.extraFilters[filter] != '' &&
                    this.extraFilters[filter] != false
                ) {
                    if (validFilters != '?') {
                        validFilters += '&';
                    }
                    validFilters += filter + '=' + this.extraFilters[filter];
                }
                console.log(this.extraFilters);
                console.log(this.extraFilters[filter]);
            }
            console.log('Valid filters: ', validFilters);
            return validFilters;
        },
        getEquipmentByCost(useExtraFilters = true) {
            // let dateRange = this.dateRange;
            // let startDate = dateRange[0];
            // let endDate = dateRange[1];
            let validFilters = this.processFilters();

            let url = `/v2/maintenance/reports/equipment-cost-by-dates/${this.invoiceType}/${this.startDate}/${this.endDate}`;
            if (validFilters != '?') {
                url += validFilters;
            }
            console.log(url);

            this.loading.totalCostByEquipment = true;
            axios
                .get(url, this.authentication)
                .then(response => {
                    this.loading.totalCostByEquipment = false;
                    console.log('data: ', response.data);
                    if (this.invoiceType == 'trailers') {
                        this.handleTrailers(response.data);
                    } else if (this.invoiceType == 'trucks') {
                        this.handleTrucks(response.data);
                    }
                })
                .catch(error => {
                    console.error('Error getting equipment data:', error);
                });
        },
        handleTrailers(data) {
            this.trailers = [];
            this.costs.filtered = data.total_cost;
            if (this.filtersActive) {
                this.costs.extraFilters = data.total_cost;
            }
            this.trailers = data.equipment.map(trailer => ({
                id: trailer.model_id,
                name:
                    trailer.model && trailer.model.Trailer
                        ? trailer.model.Trailer
                        : 'N/A',
                VIN: trailer.model ? trailer.model.VIN : 'N/A',
                total_cost: trailer.total_cost,
                percentage: Math.round(
                    (trailer.total_cost / this.costs.filtered) * 100
                )
            }));
            this.models = this.trailers;
            console.log("models", this.models);

            if (this.externalSource) {
                console.log('equipmentId', this.equipmentId);
                let equipment = this.models.filter(m => m.id == this.equipmentId);
                console.log("equipment", equipment);
                if (equipment.length) {
                    this.byCategory(equipment[0]);
                } else {
                    this.$store.dispatch('setSnackbar', {
                        color: 'warning',
                        text: 'No data found',
                    });
                    this.loading.lists = false;
                }
            }
        },
        handleTrucks(data) {
            this.trucks = [];
            this.costs.filtered = data.total_cost;
            if (this.filtersActive) {
                this.costs.extraFilters = data.total_cost;
            }
            this.trucks = data.equipment.map(truck => ({
                id: truck.model_id,
                name:
                    truck.model && truck.model.Truck
                        ? truck.model.Truck
                        : 'N/A',
                VIN: truck.model ? truck.model.VIN : 'N/A',
                total_cost: truck.total_cost,
                percentage: Math.round(
                    (truck.total_cost / this.costs.filtered) * 100
                )
            }));
            this.models = this.trucks;
        },
        /*getEquipmentByCost(useExtraFilters = true) {
            let invoiceType = this.invoiceType;
            if (invoiceType == 'supply') return;
            let dateRange = this.dateRange;
            let url = '';
            let startDate = dateRange[0];
            let endDate = dateRange[1];
            let validFilters = '';
            //Example http request query string for valid filters "?filterName=FilterContent?filterName2=FilterContent2": /maintenance/reports/equipment-cost-by-dates/trailer/2023-01-01/2024-01-01?taxRegion=Ontario?vendor=VendorName?company=CompanyName?region=RegionName?isServiceCall=true?isIncident=true?isOOCharge=true
            if (useExtraFilters) {
                validFilters = this.processFilters();
            }
            url =
                '/v2/maintenance/reports/equipment-cost-by-dates/' +
                invoiceType + //truck or trailer
                '/' +
                startDate +
                '/' +
                endDate;
            if (validFilters != '') {
                url += validFilters;
            }

            this.loading.totalCostByEquipment = true;
            this.loading.cost = true;
            this.loading.lists = true;
            axios
                .get(url, this.authentication)
                .then(response => {
                    if (invoiceType == 'trailers') {
                        this.trailers = [];
                        //Get the lowest and highest cost, convert to decimal numbers
                        let lowestCost = response.data.lowest_cost;
                        let highestCost = response.data.highest_cost;
                        this.costs.filtered = response.data.total_cost;
                        if (useExtraFilters) {
                            this.costs.extraFilters = response.data.total_cost;
                        }
                        // returns an array of trailers with their total cost "model_id" and "total_cost"
                        for (
                            let i = 0;
                            i < response.data.equipment.length;
                            i++
                        ) {
                            let trailer = response.data.equipment[i];
                            let trailerId = trailer.model_id;
                            let trailerName = trailer.model
                                ? trailer.model.Trailer
                                : 'N/A';
                            let trailerVin = trailer.model
                                ? trailer.model.VIN
                                : 'N/A';
                            let trailerTotalCost = trailer.total_cost;
                            let percentage = 0;
                            //Divide the total cost by the costs of each trailer, the trailer with the highest cost will not be 100%
                            if (this.costs.filtered != 0) {
                                percentage = Math.round(
                                    (trailerTotalCost / this.costs.filtered) *
                                        100
                                );
                            }
                            this.trailers.push({
                                TrailerId: trailerId,
                                Trailer: trailerName,
                                VIN: trailerVin,
                                total_cost: trailerTotalCost,
                                percentage: percentage
                            });
                        }
                    } else if (invoiceType == 'truck') {
                        // returns an array of trucks with their total cost "model_id" and "total_cost"
                        for (let i = 0; i < response.data.length; i++) {
                            let truck = response.data[i];
                            let truckId = truck.model_id;
                            let totalCost = truck.total_cost;
                            let truckIndex = this.trucks.findIndex(
                                truck => truck.TruckID == truckId
                            );
                            this.trucks[truckIndex].total_cost = totalCost;
                        }
                    }
                    this.loading.totalCostByEquipment = false;
                    this.loading.cost = false;
                    this.loading.lists = false;
                })
                .catch(error => {
                    console.error(error);
                    this.$store.dispatch('setSnackbar', {
                        color: 'error',
                        text: 'Error getting equipment data' + error
                    });
                });
        },*/
        getActiveFiltersStatus() {
            //Check if any of the filters are true, if so, set the filtersActive to true
            for (let filter in this.filters) {
                if (this.filters[filter] == true) {
                    this.filtersActive = true;
                    return;
                }
            }
        },
        setReportHeaders() {
            if (this.invoiceType == 'trailers') {
                this.reportHeaders = [
                    { text: 'Trailer', value: 'model.Trailer' },
                    { text: 'Invoice Number', value: 'invoice_number' },
                    { text: 'Invoice Date', value: 'invoice_issued_at' },
                    { text: 'Tax Region', value: 'tax_region.name' },
                    { text: 'Vendor', value: 'vendor.name' },
                    { text: 'Company', value: 'company.CompanyName' },
                    { text: 'Region', value: 'region.PrincipalRegion' },
                    { text: 'Service Call', value: 'is_service_call' },
                    { text: 'Incident', value: 'is_incident' },
                    { text: 'Owner Operator Charge', value: 'is_oo_charge' },
                    { text: 'SubTotal Cost', value: 'subtotal_cost' },
                    { text: 'Total', value: 'total_cost' },
                    { text: 'Expenses', value: 'data-table-expand' }
                ];
            } else if (this.invoiceType == 'truck') {
                this.reportHeaders = [
                    { text: 'Truck', value: 'model.TruckName' },
                    { text: 'Invoice Date', value: 'invoice_issued_at' },
                    { text: 'Tax Region', value: 'tax_region.name' },
                    { text: 'Vendor', value: 'vendor.name' },
                    { text: 'Company', value: 'company.CompanyName' },
                    { text: 'Region', value: 'region.PrincipalRegion' },
                    { text: 'Service Call', value: 'is_service_call' },
                    { text: 'Incident', value: 'is_incident' },
                    { text: 'Owner Operator Charge', value: 'is_oo_charge' },
                    { text: 'SubTotal Cost', value: 'subtotal_cost' },
                    { text: 'Total', value: 'total_cost' },
                    { text: 'Expenses', value: 'data-table-expand' }
                ];
            } else if (this.invoiceType == 'supply') {
                this.reportHeaders = [
                    { text: 'Supply', value: 'supply' },
                    { text: 'Invoice Number', value: 'invoice_number' },
                    { text: 'Invoice Date', value: 'invoice_issued_at' },
                    { text: 'Tax Region', value: 'tax_region.name' },
                    { text: 'Vendor', value: 'vendor.name' },
                    { text: 'Company', value: 'company.CompanyName' },
                    { text: 'Region', value: 'region.PrincipalRegion' },
                    { text: 'Service Call', value: 'is_service_call' },
                    { text: 'Incident', value: 'is_incident' },
                    { text: 'Owner Operator Charge', value: 'is_oo_charge' },
                    { text: 'SubTotal Cost', value: 'subtotal_cost' },
                    { text: 'Total', value: 'total_cost' },
                    { text: 'Expenses', value: 'data-table-expand' }
                ];
            }
        },
        async getLists() {
            this.loading.lists = true;
            await axios
                .get('/v2/maintenance/collections/report', this.authentication)
                .then(response => {
                    this.expenseTypes = response.data.expenseTypes;
                    this.expenseCategories = response.data.expenseCategories;
                    this.expenseSubCategories =
                        response.data.expenseSubCategories;
                    this.taxRegions = response.data.taxRegions;
                    this.vendors = response.data.vendors;
                    this.companies = response.data.companies;
                    this.regions = response.data.regions;
                    this.disableDateFields = false;

                    console.log('flag 1', response.data);
                    this.type = response.data.expenseTypes.find(
                        expenseType => expenseType.id == 1
                    );
                    console.log('flag 2: ', this.type);
                    this.expenseCategoriesFilteredByType(this.type);
                    this.regions.push({
                        PrincipalRegionID: 4,
                        PrincipalRegion: 'Missing data'
                    });

                    this.$store.dispatch('setSnackbar', {
                        color: 'green',
                        text: 'Data loaded successfully'
                    });
                })
                .catch(logErr)
                .finally(() => {
                    if (!this.externalSource) {
                        this.loading.lists = false;
                    }

                    this.steps.dateRange = true;
                });
        },
        async getReportData() {
            await this.getLists();
        },
        viewInvoice(invoiceId) {
            console.log(this.invoiceType);
            let invoiceType = this.invoiceType;
            let url = '';
            url =
                '/v2/maintenance/invoices/show/' +
                invoiceType +
                '/' +
                invoiceId;
            this.$store.state.isLoading = true;
            this.loading.lists = true;
            axios
                .get(url, this.authentication)
                .then(response => {
                    console.log(response);
                    this.$store.state.isLoading = false;
                    this.loading.lists = false;
                    this.invoice = response.data;
                    this.invoice.invoice_issued_at = this.formatDatetime(this.invoice.invoice_issued_at);
                    this.invoiceId = response.data.id;
                    this.invoiceDialog = true;
                })
                .catch(error => {
                    this.$store.state.isLoading = false;
                    this.loading.lists = false;
                    this.$store.dispatch('setSnackbar', {
                        color: 'error',
                        text: 'Error getting invoice data' + error
                    });
                });
        },
        formatDatetime(datetime) {
            let parts = datetime.split(' ');
            return parts[0];
        },
        downloadExpenseReport(items, type = 'csv') {
            console.log('Raw Items: ', items, 'Headers: ', this.equipmentExpensesSheetHeaders);
            console.log(this.equipment);
            let data = [];
            for (let item of items) {
                let newItem = {
                    InvoiceId: item.invoice_id,
                    InvoiceNumber: item.invoice_number, 
                    Vendor: item.vendor ? item.vendor : 'N/A', 
                    Category: item.category && item.category.name ? item.category.name : 'N/A',
                    Subcategory: item.subcategory && item.subcategory.name ? item.subcategory.name : 'N/A',
                    Description: item.description ? item.description : 'N/A', 
                    TaxRate: item.custom_tax_rate, 
                    Quantity: item.quantity, 
                    UnitCost: item.unit_cost, 
                    SubtotalCost: item.subtotal_cost, 
                    Tax: item.tax, 
                    TotalCost: item.total_cost, 
                    InvoiceDate: item.invoice_issued_at,
                };
                data.push(newItem);
            }
            let headers = [
                'InvoiceId', 'InvoiceNumber', 'Vendor', 'Category', 'Subcategory', 'Description', 'TaxRate', 'Quantity',
                'UnitCost', 'SubtotalCost', 'Tax', 'TotalCost', 'InvoiceDate'
            ];
            let totals = ['SubtotalCost', 'TotalCost'];
            let str = this.$store;
            str.state.isLoading = true;

            let params = {};
            params.Headers = headers;
            params.Data = data;
            params.Totals = {};
            for (let total of totals) {
                params.Totals[total] = this.getTotalByField(data, total);
            }
            params.ReportAlias = 'InvoiceAnalysis-ExpenseReport';
            params.Filename = 'ExpenseReport' + '-' + this.startDate + '-' + '-' + this.endDate + '-' + this.equipment.name + '.xlsx';

            this.genericDownload(params);

            // sheets.generateSheet(
            //     items,
            //     type,
            //     this.equipmentExpensesSheetHeaders,
            //     //invoiceAnalysisReport + (date range) + (equipment name)
            //     'InvoiceAnalysisReport_[' +
            //         this.startDate +
            //         '~' +
            //         this.endDate +
            //         ']_' +
            //         this.equipment.Trailer ?? this.equipment.Truck
            // );
        },
        downloadEquipmentByCostReport() {
            /**
             * Generate a report for the equipment by cost
             * Columns: Trailer, VIN, Total Cost, Percentage
             */
            let equipment = this.models;
            console.log('Equipment: ', equipment);
            let data = [];
            for (let unit of equipment) {
                let newItem = {
                    Name: unit.name,
                    TotalCost: unit.total_cost
                };
                data.push(newItem);
            }
            let headers = [
                'Name', 'TotalCost'
            ];
            let totals = ['TotalCost'];
            let str = this.$store;
            str.state.isLoading = true;

            let params = {};
            params.Headers = headers;
            params.Data = data;
            params.Totals = {};
            for (let total of totals) {
                params.Totals[total] = this.getTotalByField(data, total);
            }
            params.ReportAlias = 'InvoiceAnalysis-EquipmentByCost';
            params.Filename = 'EquipmentByCost' + '-' + this.startDate + '-' + '-' + this.endDate + '-' + '.xlsx';

            this.genericDownload(params);

            // let headers = [
            //     { text: 'Name', value: 'name' },
            //     { text: 'Total Cost', value: 'total_cost' }
            // ];
            // xlsxSheets.generateSheet(
            //     equipment,
            //     'xlsx',
            //     headers,
            //     'EquipmentByCostReport'
            // );
        },
        genericDownload(params) {
            let str = this.$store;
            str.state.isLoading = true;
            axios
                .post(
                    '/v2/maintenance/reports/generic-download', params,
                    {
                        responseType: 'blob',
                        headers: {
                            Authorization: 'Bearer ' + this.$store.state.token
                        }
                    }
                )
                .then(response => {
                    let data = response.data;
                    let url = window.URL.createObjectURL(new Blob([data]));
                    let link = document.createElement('a');
                    let filename = params.Filename;
                    link.href = url;
                    link.setAttribute('download', filename);
                    document.body.appendChild(link);
                    link.click();
                    str.state.isLoading = false;
                })
                .catch(error => {
                    console.log(error);
                    str.state.isLoading = false;
                });
        },
        downloadCategoriesByCostReport() {
            /**
             * Generate a report for the categories by cost
             * Columns: Category, Total Cost
             *
             * This information is not available in the current data,
             * we need to get the total cost for each category by calling the API
             *
             * Example response:
             * [
             * {
             * "id": 1,
             * "name": "Category Name",
             * "total_cost": "1234.56"
             * },
             * {...}
             * ]
             */

            // let headers = [
            //     { text: 'Category', value: 'name' },
            //     { text: 'Total Cost', value: 'total_cost' }
            // ];
            let str = this.$store;
            str.state.isLoading = true;
            let categories = [];

            axios
                .get(
                    '/v2/maintenance/reports/categories-by-cost/' +
                        this.invoiceType +
                        '/' +
                        this.startDate +
                        '/' +
                        this.endDate,
                    this.authentication
                )
                .then(response => {
                    categories = response.data;
                    categories.forEach(category => {
                        category.total_cost = parseFloat(
                            category.total_cost
                        ).toFixed(2);
                    });
                    console.log('Categories: ', categories);

                    let data = [];
                    for (let cat of categories) {
                        let newItem = {
                            Category: cat.name,
                            TotalCost: cat.total_cost
                        };
                        data.push(newItem);
                    }
                    let headers = [
                        'Category', 'TotalCost'
                    ];
                    let totals = ['TotalCost'];
                    let params = {};
                    params.Headers = headers;
                    params.Data = data;
                    params.Totals = {};
                    for (let total of totals) {
                        params.Totals[total] = this.getTotalByField(data, total);
                    }
                    params.ReportAlias = 'InvAnalysis-CategoriesByCost';
                    params.Filename = 'CategoriesByCost' + '-' + this.startDate + '-' + '-' + this.endDate + '-' + '.xlsx';

                    this.genericDownload(params);

                    // xlsxSheets.generateSheet(
                    //     categories,
                    //     'xlsx',
                    //     headers,
                    //     'CategoriesByCostReport'
                    // );
                })
                .catch(error => {
                    this.$store.dispatch('setSnackbar', {
                        color: 'error',
                        text: 'Error getting report data' + error
                    });
                    str.state.isLoading = true;
                });
        },
        downloadEquipmentAndCategoriesByCostReport() {
            /**
             * Generate a report for the equipment and categories by cost
             *
             * Columns: Trailer, Total Cost, Percentage, Category, Total Cost
             *
             * This information is not available in the current data,
             * we need to get the total cost for each category by calling the API
             *
             * Example response:
             * [
             *  {
             *  "name": "SUPPLIES",
             *  "total_cost": 24018.869999999995,
             *  "categories": [
             *  {
             *      "id": 9,
             *      "name": "Axels",
             *      "total_cost": "7358.40"
             *  },
             * {...}
             * ]
             * },
             * {...}
             *
             */

            let headers = [
                { text: 'Equipment', value: 'name' },
                { text: 'Total Cost', value: 'total_cost' },
                { text: 'Category', value: 'category_name' },
                { text: 'Category Total Cost', value: 'category_total_cost' }
            ];

            let equipmentAndCategories = [];

            axios
                .get(
                    '/v2/maintenance/reports/equipment-&-categories-by-cost/' +
                        this.invoiceType +
                        '/' +
                        this.startDate +
                        '/' +
                        this.endDate,
                    this.authentication
                )
                .then(response => {
                    equipmentAndCategories = response.data.flatMap(
                        equipment => {
                            return equipment.categories.map(category => {
                                return {
                                    name: equipment.name,
                                    total_cost: parseFloat(
                                        equipment.total_cost
                                    ),
                                    category_name: category.name,
                                    category_total_cost: parseFloat(
                                        category.total_cost
                                    )
                                };
                            });
                        }
                    );

                    console.log('Equipment and Categories: ', {
                        equipmentAndCategories
                    });

                    xlsxSheets.downloadPivotSheet(
                        equipmentAndCategories,
                        headers,
                        'EquipmentAndCategoriesByCostReport'
                    );
                })
                .catch(error => {
                    this.$store.dispatch('setSnackbar', {
                        color: 'error',
                        text: 'Error getting report data' + error
                    });
                });
        },
        closeInvoiceData() {
            this.invoiceFileView = false;
            this.invoiceDialog = false;
        },
        viewInvoiceFile(invoice) {
            console.log(invoice);
            if (!invoice.files.length) {
                this.$store.dispatch('setSnackbar', {
                    text: 'No files to view.',
                    color: 'warning'
                });
                return;
            }
            
            let params = {
                invoice: invoice,
            };
            this.$store.state.isLoading = true;
            axios
                .post('/v2/maintenance/invoices/files/view', params, {
                    headers: {
                        Authorization: 'Bearer ' + this.$store.state.token
                    }
                })
                .then(response => {
                    console.log(response.data);
                    this.invoiceFileLink = response.data;
                    this.invoiceFileView = true;
                    this.$store.state.isLoading = false;
                })
                .catch(error => {
                    console.log(error);
                    this.$store.state.isLoading = false;
                })
        },
        getFormattedField(value) {
            let cad = new Intl.NumberFormat('en-CA', {
                style: 'currency',
                currency: 'CAD',
            });
            return cad.format(value);
        },
        getTotalByField(source, field, format = false) {
            let total = source.reduce((acc, curr) => {
                let value = parseFloat(curr[field]);
                return acc + (!isNaN(value) ? value : 0);
            }, 0);
            total = total.toFixed(2);

            if (format) total = this.getFormattedField(total);

            return total;
        },
        getFormattedNumberField(value, decimalPlaces = 2, format = false) {
            const number = parseFloat(value);

            if (format) {
                return number.toLocaleString('en-US', {
                    minimumFractionDigits: decimalPlaces,
                    maximumFractionDigits: decimalPlaces,
                });
            } else {
                return number.toFixed(decimalPlaces);
            }
        },
        getDateFromDatetime(datetime) {
            const date = new Date(datetime);

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
            const day = String(date.getDate()).padStart(2, '0');

            return `${year}-${month}-${day}`;
        }
    },
    watch: {
        $route: {
            immediate: true,
            handler() {
                document.title = 'Maintenance Invoice Analysis';
            }
        },
        // startDate() {
        //     if (this.startDate != "" && this.endDate != "" && this.startDate < this.endDate) {
        //         this.setStep(2);
        //     }
        // },
        // endDate() {
        //     if (this.startDate != "" && this.endDate != "" && this.startDate < this.endDate) {
        //         this.setStep(2);
        //     }
        // },
        dateRange() {
            this.setDateRange();
            if (this.dateRange.length == 2) {
                this.setStep(2);
            }
        },
        equipment() {
            console.log('Equipment: ', this.equipment);
            if (this.equipment != '') {
                console.log('Equipment has a value');
                this.equipmentTotalCost = this.models.find(
                    model => model.id == this.equipment.id
                ).total_cost;
                console.log('Equipment total cost: ', this.equipmentTotalCost);
                this.filteredCost = this.equipmentTotalCost;
                console.log('Filtered total cost: ', this.filteredCost);
            }
        },
        category() {
            this.changeFilter('category');
        },
        subcategory() {
            this.changeFilter('subcategory');
        },
        // filterBy() {
        //     //Reset the extra filters contents if the filter is not in the filterBy array
        //     for (let filter in this.filters) {
        //         if (!this.filterBy.includes(filter)) {
        //             this.extraFilters[filter] = '';
        //             console.log('Extra filters: ', this.extraFilters);
        //         }
        //     }
        // }
    }
};
</script>
<style>
    td {
        text-align: left;
    }
</style>